import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { debounce } from "lodash";
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    InputAdornment,
    Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import { AcxAgentService, ChatSession } from "../../../../../services/AcxAgentService";
import AcxTextField from "../../../../UI/AcxTextField";
import AcxChip from "../../../../UI/AcxChip";
import AcxLoadingIndicator from "../../../../UI/AcxLoadingIndicator";
import AcxEmptyState from "../../../../UI/AcxEmptyStateIndicator/AcxEmptyState";

const agentService = new AcxAgentService();

type Props = {
    organizationId: string;
    selectedChatId: string | null;
    onChatSelect: (chatId: string, chatSession: ChatSession) => void;
};

const ITEMS_PER_PAGE = 20;

const ChatHistoryList: React.FC<Props> = observer(({ organizationId, selectedChatId, onChatSelect }) => {
    const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [filter, setFilter] = useState<"all" | "non-authentizens" | "authentizens">("all");
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    
    const observerRef = useRef<IntersectionObserver | null>(null);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);
    const currentChatCountRef = useRef(0);
    const loadingPageRef = useRef(false);
    const requestVersionRef = useRef(0);
    
    const loadChatSessions = useCallback(async (isInitial: boolean = true, filterOverride?: string, searchOverride?: string) => {
        if (!isInitial && loadingPageRef.current) {
            return;
        }

        const requestVersion = isInitial
            ? ++requestVersionRef.current
            : requestVersionRef.current;
        loadingPageRef.current = true;

        if (isInitial) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        setError(null);
        
        try {
            const startDate = new Date(2024, 0, 1).toISOString();
            const endDate = new Date().toISOString();
            
            const skip = isInitial ? 0 : currentChatCountRef.current;
            
            const currentFilter = filterOverride !== undefined ? filterOverride : filter;
            const currentSearch = searchOverride !== undefined ? searchOverride : debouncedSearchQuery;
            
            // Build search string based on filter and search query
            let searchString: string | undefined = currentSearch || undefined;
            
            // If there's a filter active, combine it with the search
            if (currentFilter === "authentizens") {
                searchString = currentSearch ? `${currentSearch} authenticx` : "authenticx";
            } else if (currentFilter === "non-authentizens") {
                searchString = currentSearch ? `${currentSearch} !authenticx` : "!authenticx";
            }
            
            // Try to use paginated endpoint first, fallback to getall if it doesn't exist
            try {
                const response = await agentService.getChatsPaginated(
                    startDate,
                    endDate,
                    organizationId,
                    searchString,
                    skip,
                    ITEMS_PER_PAGE
                );
                
                if (requestVersion !== requestVersionRef.current) {
                    return;
                }

                if (isInitial) {
                    setChatSessions(response.chats);
                    currentChatCountRef.current = response.chats.length;
                } else {
                    setChatSessions(prev => {
                        const existingChatIds = new Set(prev.map(chat => chat.id));
                        const uniqueNewChats = response.chats.filter(chat => !existingChatIds.has(chat.id));
                        const newChats = [...prev, ...uniqueNewChats];
                        currentChatCountRef.current = newChats.length;
                        return newChats;
                    });
                }
                
                setTotalCount(response.totalCount);
                setHasMore(skip + response.chats.length < response.totalCount);
            } catch (paginatedError) {
                // Fallback to getAllChats if paginated endpoint doesn't exist
                if (isInitial) {
                    const sessions = await agentService.getAllChats(startDate, endDate, organizationId);
                    if (requestVersion !== requestVersionRef.current) {
                        return;
                    }

                    // Apply client-side filtering for fallback
                    const filtered = sessions.filter(session => {
                        let matchesFilter = true;
                        if (currentFilter === "authentizens") {
                            matchesFilter = session.username.includes("authenticx");
                        } else if (currentFilter === "non-authentizens") {
                            matchesFilter = !session.username.includes("authenticx");
                        }
                        
                        let matchesSearch = true;
                        if (currentSearch) {
                            const searchLower = currentSearch.toLowerCase();
                            matchesSearch = session.username.toLowerCase().includes(searchLower) ||
                                          `${session.firstName} ${session.lastName}`.toLowerCase().includes(searchLower);
                        }
                        
                        return matchesFilter && matchesSearch;
                    });
                    setChatSessions(filtered);
                    currentChatCountRef.current = filtered.length;
                    setTotalCount(filtered.length);
                    setHasMore(false); // No pagination with getAllChats
                }
            }
        } catch (err) {
            if (requestVersion === requestVersionRef.current) {
                setError(err instanceof Error ? err.message : "Failed to load chat sessions");
            }
        } finally {
            if (requestVersion === requestVersionRef.current) {
                loadingPageRef.current = false;
                setLoading(false);
                setLoadingMore(false);
            }
        }
    }, [organizationId, filter, debouncedSearchQuery]);
    
    // Create debounced search handler
    const debouncedSearch = useMemo(
        () => debounce((searchValue: string) => {
            setDebouncedSearchQuery(searchValue);
        }, 1000),
        []
    );

    // Update debounced search when search query changes
    useEffect(() => {
        debouncedSearch(searchQuery);
        return () => {
            debouncedSearch.cancel();
        };
    }, [searchQuery, debouncedSearch]);

    // Load when organizationId, filter, or debouncedSearchQuery changes
    useEffect(() => {
        // Reset pagination when filter or search changes
        setChatSessions([]);
        currentChatCountRef.current = 0;
        setHasMore(true);
        loadChatSessions(true);
    }, [organizationId, filter, debouncedSearchQuery, loadChatSessions]);
    
    // Set up intersection observer for infinite scroll
    useEffect(() => {
        if (observerRef.current) {
            observerRef.current.disconnect();
        }
        
        observerRef.current = new IntersectionObserver((entries) => {
            const lastEntry = entries[0];
            if (lastEntry.isIntersecting && hasMore && !loadingMore && !loading) {
                loadChatSessions(false);
            }
        }, {
            threshold: 0.1
        });
        
        if (loadMoreRef.current) {
            observerRef.current.observe(loadMoreRef.current);
        }
        
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [hasMore, loadingMore, loading, loadChatSessions]);
    
    // Remove client-side filtering - all filtering is done on the backend
    const filteredChatSessions = chatSessions;

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                <Typography variant="h6" gutterBottom>
                    Chat History
                </Typography>
                <AcxTextField
                    id="chat-search"
                    type="text"
                    fullWidth
                    label=""
                    placeholder="Search by username or name..."
                    helperText=""
                    error={false}
                    required={false}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    other={{
                        value: searchQuery,
                        InputProps: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        },
                        sx: { mb: 2 }
                    }}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                    {(["all", "non-authentizens", "authentizens"] as const).map((status) => (
                        <AcxChip
                            key={status}
                            label={status.charAt(0).toUpperCase() + status.slice(1)}
                            size="small"
                            onClick={() => setFilter(status)}
                            color={filter === status ? "blue" : "gray"}
                            clickable
                        />
                    ))}
                </Box>
                {totalCount > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        Showing {filteredChatSessions.length} of {totalCount} chats
                    </Typography>
                )}
            </Box>
            
            <Box sx={{ flex: 1, overflow: "auto" }}>
                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
                        {error}
                    </Alert>
                )}
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                        <AcxLoadingIndicator size={24} />
                    </Box>
                ) : filteredChatSessions.length === 0 ? (
                    <AcxEmptyState
                        size="small"
                        variant="empty"
                        overrideTitle={searchQuery || filter !== "all" 
                            ? "No chats found matching your criteria" 
                            : "No chat sessions found"}
                        overrideSubTitle={searchQuery || filter !== "all" 
                            ? "Try adjusting your search or filters" 
                            : "No chat sessions found in the selected date range"}
                        height={300}
                    />
                ) : (
                    <List sx={{ p: 0 }}>
                        {filteredChatSessions.map((session) => (
                            <ListItem key={session.id} disablePadding>
                                <ListItemButton
                                    selected={selectedChatId === session.id}
                                    onClick={() => onChatSelect(session.id, session)}
                                    sx={{
                                        borderBottom: 1, 
                                        borderColor: "divider",
                                        "&:hover": {
                                            backgroundColor: "action.hover",
                                        },
                                    }}
                                >
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <Typography variant="subtitle2">
                                                    {session.firstName} {session.lastName}
                                                </Typography>
                                                {session.messages.some(m => m.userFeedbackType || m.userFeedback) && <AcxChip
                                                    label="Feedback"
                                                    size="small"
                                                    color="orange"
                                                />}
                                            </Box>
                                        }
                                        secondary={
                                            <Box>
                                                <Typography variant="caption">
                                                    {session.username}
                                                </Typography>
                                                <Typography variant="caption" display="block">
                                                    {moment(session.createdOn).format("MMM DD, YYYY HH:mm")} ({session.messages.length} messages)
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                        {/* Infinite scroll trigger */}
                        {hasMore && (
                            <Box ref={loadMoreRef} sx={{ p: 2, textAlign: "center" }}>
                                {loadingMore ? (
                                    <AcxLoadingIndicator size={24} />
                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        Scroll to load more...
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </List>
                )}
            </Box>
        </Box>
    );
});

export default ChatHistoryList;
